/**
 * Ctrl-K as an action launcher: "gia hạn example.vn", "renew example.vn", "đổi ns tino.vn",
 * "mã chuyển example.com". A leading verb (Vietnamese with or without diacritics, or English)
 * followed by a domain name becomes that action on that domain, opened on the domain page
 * (`/domains/<name>?action=<id>`); "chuyển về example.com" opens the transfer-in dialog
 * ([`commandTarget`]). Plain code with no imports so node tests can load it.
 */

export type DomainCommand = 'renew' | 'nameservers' | 'dnssec' | 'lock' | 'transferCode' | 'registrant' | 'restore' | 'transferIn';

/** The actions offered for a domain found by name, in this order. */
export const DOMAIN_ACTIONS: readonly DomainCommand[] = ['renew', 'nameservers', 'lock', 'transferCode', 'dnssec', 'registrant', 'restore'];

/** Where a command opens: an action of a domain the platform holds on its page; a transfer-in
 * (a domain not held yet) in the "Chuyển tên miền về" dialog of the home page. */
export function commandTarget(c: { action: DomainCommand; domain: string }): string {
  const d = encodeURIComponent(c.domain);
  return c.action === 'transferIn' ? `/?action=transfer-in&domain=${d}` : `/domains/${d}?action=${c.action}`;
}

/** Verbs (lower case, without diacritics), longest first so "doi chu the" wins over "doi". */
const VERBS: readonly [string, DomainCommand][] = [
  ['doi may chu ten', 'nameservers'],
  ['chuyen ten mien ve', 'transferIn'],
  ['transfer in', 'transferIn'],
  ['chuyen ve', 'transferIn'],
  ['khoi phuc', 'restore'],
  ['restore', 'restore'],
  ['doi chu the', 'registrant'],
  ['may chu ten', 'nameservers'],
  ['transfer code', 'transferCode'],
  ['nameservers', 'nameservers'],
  ['nameserver', 'nameservers'],
  ['registrant', 'registrant'],
  ['ma chuyen', 'transferCode'],
  ['auth code', 'transferCode'],
  ['mo khoa', 'lock'],
  ['gia han', 'renew'],
  ['doi ns', 'nameservers'],
  ['unlock', 'lock'],
  ['dnssec', 'dnssec'],
  ['renew', 'renew'],
  ['authinfo', 'transferCode'],
  ['khoa', 'lock'],
  ['lock', 'lock'],
  ['ns', 'nameservers'],
];

function fold(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();
}

/** A name with at least one dot: "example.vn", "tên-miền.vn". */
const DOMAIN_RE = /^[\p{L}\p{N}](?:[\p{L}\p{N}-]{0,62})(?:\.[\p{L}\p{N}-]{1,63})+\.?$/u;

/** "gia hạn example.vn" → { action: 'renew', domain: 'example.vn' }; null when it is not such a command. */
export function parseDomainCommand(query: string): { action: DomainCommand; domain: string } | null {
  const raw = query.trim().replace(/\s+/g, ' ');
  const folded = fold(raw);
  for (const [verb, action] of VERBS) {
    if (!folded.startsWith(`${verb} `)) continue;
    // Cut the typed text by the verb's word count (its diacritics may differ from the fold).
    const words = verb.split(' ').length;
    const domain = raw.split(' ').slice(words).join(' ').trim().toLowerCase().replace(/\.$/, '');
    if (!DOMAIN_RE.test(domain)) return null;
    return { action, domain };
  }
  return null;
}
